/**
 * useEventRsvpAdmin Hooks
 *
 * Admin hooks for managing event RSVPs using URQL.
 */

import { useCallback } from "react";
import { useQuery, useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const AdminEventPendingRsvpsDocument = `
  query AdminEventPendingRsvps($eventId: ID!) {
    adminEventPendingRsvps(eventId: $eventId) {
      id
      accountId
      account {
        id
        firstName
        lastName
      }
    }
  }
`;

const ConfirmEventRsvpDocument = `
  mutation ConfirmEventRsvp($eventId: ID!, $accountId: ID!) {
    confirmEventRsvp(eventId: $eventId, accountId: $accountId) {
      id
      confirmed
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface PendingRsvp {
  id: string;
  accountId: string;
  account: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
}

interface UseAdminEventPendingRsvpsOptions {
  eventId: string | undefined;
  pause?: boolean;
}

interface UseAdminEventPendingRsvpsResult {
  pendingRsvps: PendingRsvp[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseConfirmEventRsvpResult {
  confirmRsvp: (eventId: string, accountId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch pending RSVPs for an event.
 */
export function useAdminEventPendingRsvps(
  options: UseAdminEventPendingRsvpsOptions
): UseAdminEventPendingRsvpsResult {
  const { eventId, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    adminEventPendingRsvps: PendingRsvp[];
  }>({
    query: AdminEventPendingRsvpsDocument,
    variables: { eventId: eventId ?? "" },
    pause: pause || !eventId,
  });

  return {
    pendingRsvps: result.data?.adminEventPendingRsvps ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

/**
 * Confirm an event RSVP.
 */
export function useConfirmEventRsvp(): UseConfirmEventRsvpResult {
  const [result, executeMutation] = useMutation<
    { confirmEventRsvp: { id: string; confirmed: boolean } },
    { eventId: string; accountId: string }
  >(ConfirmEventRsvpDocument);

  const confirmRsvp = useCallback(
    async (eventId: string, accountId: string): Promise<void> => {
      try {
        const response = await executeMutation({ eventId, accountId });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to confirm RSVP");
          throw new Error(message);
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "RSVP confirmed",
          data: { eventId, accountId },
        });
      } catch (err) {
        const confirmError =
          err instanceof Error ? err : new Error("Failed to confirm RSVP");

        errorTracking.captureError(confirmError, {
          tags: { feature: "admin", action: "confirmRsvp" },
        });

        throw confirmError;
      }
    },
    [executeMutation]
  );

  return {
    confirmRsvp,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
